import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Battery, Gauge, Zap } from "lucide-react";

export default function ProductCard({ product, index = 0 }) {
  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="group relative bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col hover:border-[#2A8BFF]/40 hover:shadow-[0_18px_50px_rgba(11,19,32,0.08)] transition-all"
    >
      <Link to={`/products/${product.slug}`} className="block">
        <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#F6F8FB] via-white to-[#EEF3FA]">
          <div className="absolute inset-0 helix-bg opacity-50" />
          <img
            src={product.image}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-contain p-6 transition-transform duration-700 group-hover:scale-110"
            loading="lazy"
          />
          <div className="absolute top-4 left-4 text-[10px] tracking-[0.22em] uppercase font-bold text-[#1E6FE5] border border-[#1E6FE5]/30 px-2.5 py-1 bg-white/80 backdrop-blur-sm rounded-full">
            {product.categoryLabel}
          </div>
          <div className="absolute top-4 right-4 text-[10px] tracking-[0.18em] uppercase text-slate-700 bg-white/80 backdrop-blur-sm border border-slate-200 px-2.5 py-1 rounded-full">
            {product.color}
          </div>
        </div>
      </Link>

      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              {product.tagline}
            </p>
            <h3 className="font-outfit font-bold text-2xl mt-1 text-slate-900">{product.name}</h3>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-5">
          <Spec icon={<Gauge size={14} />} label="Range" value={product.keySpecs.range} />
          <Spec icon={<Zap size={14} />} label="Speed" value={product.keySpecs.speed} />
          <Spec icon={<Battery size={14} />} label="Battery" value={product.keySpecs.battery} />
        </div>

        <Link
          to={`/products/${product.slug}`}
          data-testid={`product-card-${product.slug}-cta`}
          className="mt-6 inline-flex items-center justify-between border-t border-slate-200 pt-4 text-sm font-bold text-slate-900 hover:text-[#2A8BFF] transition-colors"
        >
          View details
          <ArrowUpRight
            size={18}
            className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
          />
        </Link>
      </div>
    </motion.article>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div className="border border-slate-200 bg-[#F6F8FB] rounded-lg px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-slate-500 text-[10px] uppercase tracking-[0.16em] font-bold">
        <span className="text-[#1E6FE5]">{icon}</span>
        {label}
      </div>
      <p className="text-slate-900 text-xs mt-1 font-bold">{value}</p>
    </div>
  );
}
